'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type {ComponentProps, FC, ReactNode} from 'react';
import {useAfterFirstPaint} from '../../hooks';
import {AppIcon, FaQuoteIcon, mirrorRtl} from '../icon';
import {Button, Typography, type TypographyProps} from '../primitives';
import {RegisterInterestCta} from '../register-interest/RegisterInterestCta';

export type BrandQuoteCta = {kind: 'link'; label: string; href: string} | {kind: 'registerInterest'};

export type BrandHeadlineVariant = 'bold' | 'italic';

export interface BrandQuoteSectionProps {
  title: string;
  variant?: BrandHeadlineVariant;
  cta: BrandQuoteCta;
  image: string;
  onCtaClick?: () => void;
}

export const QuoteRoot = styled('section')(({theme}) => ({
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: 360,
  [theme.breakpoints.up('md')]: {minHeight: 520},
})) as FC<{children?: ReactNode}>;

export const COVER_SIZES = '100vw';

export const QUOTE_TITLE: Record<
  BrandHeadlineVariant,
  Pick<TypographyProps, 'size' | 'weight' | 'fontStyle' | 'mobileSize' | 'textTransform'>
> = {
  bold: {size: 'headline-1', weight: 'bold', mobileSize: 'title-4', textTransform: 'uppercase'},
  italic: {size: 'headline-1', weight: 'semiBold', fontStyle: 'italic', mobileSize: 'title-4'},
};

export const QuoteCover = styled(NextImage)({
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  objectFit: 'cover',
  objectPosition: 'center',
}) as FC<{src: string; alt: string; fill: true; sizes: string}>;

const Content = styled(Box)(({theme}) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
  paddingBlock: theme.spacing(10),
}));

const Card = styled(Box)(({theme}) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2.25),
  width: '100%',
  maxWidth: 370,
  padding: theme.spacing(5.25, 3, 6),
  background: theme.palette.colors.surface.darkOverlay,
  borderRadius: theme.radii['2xl'],
  [theme.breakpoints.up('md')]: {maxWidth: 620, borderRadius: 40, paddingInline: theme.spacing(5)},
}));

const Scrim = styled(Box)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  background: theme.palette.colors.surface.darkOverlay,
}));

const QuoteMark = styled(AppIcon, {shouldForwardProp: propName => propName !== 'placement'})<{
  placement: 'open' | 'close';
}>(({theme, placement}) => ({
  position: 'absolute',
  color: theme.palette.colors.text.neutral.inverse,
  ...(placement === 'open'
    ? {top: -4, insetInlineStart: -20, transform: 'rotate(180deg)', '[dir="rtl"] &': {transform: 'rotate(180deg) scaleX(-1)'}}
    : {bottom: -4, insetInlineEnd: -20, ...mirrorRtl}),
})) as FC<ComponentProps<typeof AppIcon> & {placement: 'open' | 'close'}>;

export function BrandQuoteCtaButton({cta, onCtaClick}: {cta: BrandQuoteCta; onCtaClick?: () => void}) {
  if (cta.kind === 'registerInterest') {
    return <RegisterInterestCta variant="contained-light" onClick={onCtaClick} />;
  }
  return (
    <Button variant="contained-light" component={NextLink} href={cta.href} onClick={onCtaClick}>
      {cta.label}
    </Button>
  );
}

export function BrandQuoteSection({title, variant = 'bold', cta, image, onCtaClick}: BrandQuoteSectionProps) {
  const showImage = useAfterFirstPaint();
  return (
    <QuoteRoot>
      {showImage && <QuoteCover src={image} alt="" fill sizes={COVER_SIZES} />}
      <Scrim />
      <Container>
        <Content>
          <Card>
            <QuoteMark icon={FaQuoteIcon} placement="open" style={{fontSize: 40}} aria-hidden />
            <Typography family="primary" {...QUOTE_TITLE[variant]} as="h2" sx={{color: theme => theme.palette.colors.text.neutral.inverse}}>
              {title}
            </Typography>
            <BrandQuoteCtaButton cta={cta} onCtaClick={onCtaClick} />
            <QuoteMark icon={FaQuoteIcon} placement="close" style={{fontSize: 40}} aria-hidden />
          </Card>
        </Content>
      </Container>
    </QuoteRoot>
  );
}

BrandQuoteSection.displayName = 'BrandQuoteSection';
